import { AssetColumn, AssetColumnKind, AssetInventoryType, AssetManifestColumnWire } from 'models'

const { String: Str, Boolean: Bool, Array: Arr, Timestamp: Ts, Number: Num } = AssetColumnKind

/**
 * The predicates the records endpoint declares per column kind. The shared colDef generator reads
 * these to pick a filter widget — gte_/lte_ without any_ is a date range, a lone equal_ a boolean
 * — and the server rejects any predicate a column does not declare.
 */
export const PREDICATES: Record<AssetColumnKind, string[]> = {
  [Str]: ['any_', 'contains_'],
  [Arr]: ['array_contains_', 'array_like_'],
  [Bool]: ['equal_'],
  [Ts]: ['gte_', 'lte_', 'between_'],
  [Num]: ['gte_', 'lte_', 'between_']
}

/* Arrays have no scalar to order by, so the server refuses to sort on them. */
const column = (
  name: string,
  displayName: string,
  kind: AssetColumnKind,
  activeByDefault: boolean
): AssetColumn => ({
  name,
  displayName,
  kind,
  activeByDefault,
  sortable: kind !== Arr,
  searchable: kind !== Ts && kind !== Num
})

/**
 * The Source column: product slugs, filtered with `any_` only (no `contains_`, no `no_value_`).
 * Not a manifest column, and neither sortable nor searchable.
 */
export const SOURCE_COLUMN: AssetColumn = {
  name: 'SOURCES',
  displayName: 'Source',
  kind: Str,
  activeByDefault: true,
  sortable: false,
  searchable: false
}

const KIND_BY_TYPE: Record<string, AssetColumnKind> = {
  string: Str,
  varchar: Str,
  boolean: Bool,
  bool: Bool,
  array: Arr,
  timestamp: Ts,
  timestamp_ntz: Ts,
  datetime: Ts,
  date: Ts,
  number: Num,
  integer: Num,
  int: Num,
  float: Num,
  double: Num,
  long: Num
}

const kindOf = (type: string | undefined): AssetColumnKind => KIND_BY_TYPE[(type ?? '').toLowerCase()] ?? Str

/* AIM columns the manifest leaves unlabelled; the grid still offers them, off by default. */
const UNLABELLED_COLUMNS: Record<AssetInventoryType, { name: string; displayName: string; kind: AssetColumnKind }[]> = {
  [AssetInventoryType.Devices]: [{ name: 'GROUP_IDS', displayName: 'Group ID', kind: Arr }],
  [AssetInventoryType.Identities]: [{ name: 'USER_CREATION_DATE', displayName: 'Created', kind: Ts }]
}

/**
 * The tab's grid columns: every labelled manifest column the records row carries, in manifest
 * order. Label, default visibility and kind all come from the manifest, so disagreements are fixed
 * there. `rowKeys` is null when there is no row to check against (an empty or unbuilt table), and
 * then every labelled column but the `ONYXIA_*` provenance ones is kept.
 */
export const toAssetColumns = (
  assetType: AssetInventoryType,
  manifestColumns: AssetManifestColumnWire[],
  rowKeys: string[] | null
): AssetColumn[] => {
  const inRow = (name: string) => (rowKeys ? rowKeys.includes(name) : !name.startsWith('ONYXIA_'))

  const labelled = manifestColumns
    .filter((wire) => !!wire.displayName && inRow(wire.name))
    .map((wire) => column(wire.name, wire.displayName as string, kindOf(wire.type), !!wire.displayByDefault))

  const unlabelled = UNLABELLED_COLUMNS[assetType]
    .filter((extra) => inRow(extra.name) && !labelled.some((item) => item.name === extra.name))
    .map((extra) => {
      const wire = manifestColumns.find((item) => item.name === extra.name)

      return column(extra.name, extra.displayName, wire ? kindOf(wire.type) : extra.kind, false)
    })

  return [...labelled, ...unlabelled]
}

/**
 * The details pane orders attributes for reading, not to mirror the grid. Only Identities is drawn
 * in the design, and it omits a few manifest columns — those follow, so nothing is dropped.
 */
const IDENTITY_PANE_ORDER = [
  'USER_ID',
  'EMAILS_ADDRESSES',
  'FIRST_NAME',
  'LAST_NAME',
  'GROUP_NAMES',
  'DEPARTMENTS',
  'LOCATION',
  'USER_STATUS',
  'USER_ROLES',
  'ADMIN_STATUS',
  'LAST_LOGIN',
  'PASSWORD_LAST_UPDATED',
  'IS_BYPASS',
  'HAS_MFA'
]

export const paneColumnsFor = (assetType: AssetInventoryType, columns: AssetColumn[]): AssetColumn[] => {
  if (assetType !== AssetInventoryType.Identities) return columns

  const rank = (column: AssetColumn) => {
    const index = IDENTITY_PANE_ORDER.indexOf(column.name)

    return index === -1 ? IDENTITY_PANE_ORDER.length : index
  }

  return [...columns].sort((a, b) => rank(a) - rank(b))
}

/* The column the Last Seen window filters on, and the one the tab's control is named after. */
export const RECENCY_COLUMN: Record<AssetInventoryType, string> = {
  [AssetInventoryType.Identities]: 'LAST_LOGIN',
  [AssetInventoryType.Devices]: 'LAST_SEEN'
}
